import React, { useState } from 'react';
import * as Fi from 'react-icons/fi';
import ConfirmModal from './ConfirmModal.jsx';
import TimerSheet from './TimerSheet.jsx';
import SettingsSheet from './SettingsSheet.jsx';
import LetSheet from './LetSheet.jsx';
import DisputeSheet from './DisputeSheet.jsx';
import PricingModal from './PricingModal.jsx';

/* ── Game dots ── */
const Dots = ({ count, filled, color }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="rounded-full transition-all"
        style={{
          width: 6,
          height: 6,
          backgroundColor: i < filled ? color : 'rgba(255,255,255,0.15)',
          boxShadow: i < filled ? `0 0 4px ${color}` : 'none',
        }}
      />
    ))}
  </div>
);

/* ── Elapsed timer ── */
const Elapsed = ({ elapsed }) => {
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return (
    <span className="font-mono font-black text-emerald-300 tabular-nums" style={{ fontSize: 12 }}>
      {mm}:{ss}
    </span>
  );
};

/* ── Centre match info ── */
const MatchInfo = ({ game, bestOf, pts, gamesNeeded, leftName, rightName, leftGames, rightGames, leftColor, rightColor }) => (
  <div className="flex flex-col items-center justify-center gap-1 min-w-0">
    <span
      style={{
        fontSize: 10,
        fontWeight: 900,
        color: 'rgba(255,255,255,0.45)',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}
    >
      第{game}局 · {bestOf}局{bestOf > 1 ? '制' : ''} · 先得 {pts} 分
    </span>
    <div className="flex items-center gap-1.5">
      <span className="font-black truncate" style={{ maxWidth: 50, fontSize: 10, color: leftColor }}>
        {leftName}
      </span>
      <Dots count={gamesNeeded} filled={leftGames} color={leftColor} />
      <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: 10 }}>:</span>
      <Dots count={gamesNeeded} filled={rightGames} color={rightColor} />
      <span className="font-black truncate" style={{ maxWidth: 50, fontSize: 10, color: rightColor }}>
        {rightName}
      </span>
    </div>
  </div>
);

/* ── Action button list ── */
const actionList = (handlers, isPlaying) => [
  ...(isPlaying ? [{ label: '改判', icon: '⚖️', color: 'amber', onClick: handlers.let }] : []),
  { label: '換邊', icon: <Fi.FiRepeat size={12} />, color: 'amber', onClick: handlers.swap },
  { label: '記錄', icon: <Fi.FiList size={12} />, color: 'white', onClick: handlers.log },
  { label: '統計', icon: <Fi.FiBarChart2 size={12} />, color: 'indigo', onClick: handlers.stats },
  { label: '設定', icon: <Fi.FiSettings size={12} />, color: 'white', onClick: handlers.settings },
  { label: '重置', icon: <Fi.FiPower size={12} />, color: 'rose', onClick: handlers.reset },
];

const btnClass = color =>
  `flex-1 flex items-center justify-center gap-1 h-8 rounded-xl font-black text-[10px] active:scale-95 transition-all border ${
    color === 'amber'
      ? 'bg-amber-500/15 border-amber-500/30 text-amber-200'
      : color === 'rose'
      ? 'bg-rose-500/15 border-rose-500/30 text-rose-300'
      : color === 'indigo'
      ? 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300'
      : 'bg-white/[0.07] border-white/12 text-white/60'
  }`;

const TopBar = ({
  elapsed,
  game,
  gamesA,
  gamesB,
  gamesNeeded,
  canUndo,
  onUndo,
  onSwap,
  onReset,
  onShowLog,
  pts,
  bestOf,
  teamNameA,
  teamNameB,
  swapped,
  settings,
  onSettingsSave,
  onLet,
  onDispute,
  currentServer,
  isPlaying,
  onShowStats,
}) => {
  const [confirmReset, setConfirmReset] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLet, setShowLet] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const leftColor = swapped ? '#fb7185' : '#818cf8';
  const rightColor = swapped ? '#818cf8' : '#fb7185';
  const leftName = swapped ? teamNameB : teamNameA;
  const rightName = swapped ? teamNameA : teamNameB;
  const leftGames = swapped ? gamesB : gamesA;
  const rightGames = swapped ? gamesA : gamesB;

  const handleLet = () => {
    setShowLet(false);
    onLet?.();
  };

  const handleDisputeOpen = () => {
    setShowLet(false);
    setTimeout(() => setShowDispute(true), 200);
  };

  const closeActions = () => setShowActions(false);

  const handlers = {
    let: () => {
      setShowLet(true);
      closeActions();
    },
    swap: () => {
      onSwap();
      closeActions();
    },
    log: () => {
      onShowLog();
      closeActions();
    },
    stats: () => {
      onShowStats?.();
      closeActions();
    },
    settings: () => {
      setShowSettings(true);
      closeActions();
    },
    reset: () => {
      setConfirmReset(true);
      closeActions();
    },
  };

  const infoProps = { game, bestOf, pts, gamesNeeded, leftName, rightName, leftGames, rightGames, leftColor, rightColor };

  return (
    <>
      <nav
        className="shrink-0 z-50 relative border-b border-white/[0.08] bg-black/65 backdrop-blur-md"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* ══════════════════════════════════════
            手機 + iPad (< 1280px)
            ══════════════════════════════════════ */}
        <div className="flex xl:hidden items-center gap-1.5 px-2.5 h-11">
          {/* 撤銷 */}
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 border border-white/15 text-white/80 disabled:opacity-25 active:scale-90 transition-all shrink-0"
          >
            <Fi.FiCornerUpLeft size={14} />
          </button>

          {/* 計時 */}
          <div className="flex items-center gap-1 px-2 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 shrink-0">
            <Fi.FiClock size={11} className="text-emerald-400" />
            <Elapsed elapsed={elapsed} />
          </div>

          {/* 中間資訊 */}
          <div className="flex-1 flex items-center justify-center min-w-0">
            <MatchInfo {...infoProps} />
          </div>

          {/* 贊助按鈕 */}
          <button
            onClick={() => setShowPricing(true)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-300 active:scale-90 transition-all shrink-0"
            title="贊助支持"
          >
            💝
          </button>

          {/* 計時器 */}
          <button
            onClick={() => setShowTimer(true)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/12 border border-emerald-500/25 text-emerald-300 active:scale-90 transition-all shrink-0"
          >
            <Fi.FiClock size={13} />
          </button>

          {/* 更多 */}
          <button
            onClick={() => setShowActions(v => !v)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-all shrink-0 ${
              showActions ? 'bg-white/20 border-white/30 text-white' : 'bg-white/10 border-white/15 text-white/70'
            }`}
          >
            <Fi.FiMoreVertical size={14} />
          </button>
        </div>

        {/* 展開列 */}
        {showActions && (
          <div className="flex xl:hidden items-center gap-1.5 px-2.5 pb-2 animate-fadeIn flex-wrap">
            {actionList(handlers, isPlaying).map(btn => (
              <button key={btn.label} onClick={btn.onClick} className={btnClass(btn.color)}>
                <span className="text-sm leading-none">{typeof btn.icon === 'string' ? btn.icon : btn.icon}</span>
                {btn.label}
              </button>
            ))}
          </div>
        )}

        {/* ══════════════════════════════════════
            大桌面版 (≥ 1280px)
            ══════════════════════════════════════ */}
        <div className="hidden xl:flex items-center gap-2 px-4 h-13" style={{ height: 52 }}>
          {/* 左群組 */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onUndo}
              disabled={!canUndo}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white/10 border border-white/15 text-white/80 disabled:opacity-25 hover:bg-white/15 transition-all text-sm font-black"
            >
              <Fi.FiCornerUpLeft size={14} />
              撤銷
            </button>
            <div className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 shrink-0">
              <Fi.FiClock size={13} className="text-emerald-400" />
              <Elapsed elapsed={elapsed} />
            </div>
          </div>

          {/* 中間 */}
          <div className="flex-1 flex items-center justify-center min-w-0">
            <MatchInfo {...infoProps} />
          </div>

          {/* 右群組 */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setShowPricing(true)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 font-black text-sm hover:bg-rose-500/25 transition-all"
            >
              💝 贊助
            </button>
            {isPlaying && (
              <button
                onClick={() => setShowLet(true)}
                className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-200 font-black text-sm hover:bg-amber-500/25 transition-all"
              >
                ⚖️ 改判
              </button>
            )}
            <button
              onClick={() => setShowTimer(true)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-black text-sm hover:bg-emerald-500/20 transition-all"
            >
              <Fi.FiClock size={14} />
              計時
            </button>
            <button
              onClick={onSwap}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-200 font-black text-sm hover:bg-amber-500/20 transition-all"
            >
              <Fi.FiRepeat size={14} />
              換邊
            </button>
            <button
              onClick={onShowLog}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white/[0.07] border border-white/12 text-white/60 font-black text-sm hover:bg-white/12 transition-all"
            >
              <Fi.FiList size={14} />
              記錄
            </button>
            <button
              onClick={() => onShowStats?.()}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 font-black text-sm hover:bg-indigo-500/25 transition-all"
            >
              <Fi.FiBarChart2 size={14} />
              統計
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white/[0.07] border border-white/12 text-white/60 font-black text-sm hover:bg-white/12 transition-all"
            >
              <Fi.FiSettings size={14} />
              設定
            </button>
            <button
              onClick={() => setConfirmReset(true)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-rose-500/12 border border-rose-500/25 text-rose-300 font-black text-sm hover:bg-rose-500/20 transition-all"
            >
              <Fi.FiPower size={14} />
              重置
            </button>
          </div>
        </div>
      </nav>

      <TimerSheet open={showTimer} onClose={() => setShowTimer(false)} />
      <SettingsSheet open={showSettings} onClose={() => setShowSettings(false)} settings={settings} onSave={onSettingsSave} />
      <LetSheet open={showLet} onClose={() => setShowLet(false)} onLet={handleLet} onDispute={handleDisputeOpen} />
      <DisputeSheet
        open={showDispute}
        onClose={() => setShowDispute(false)}
        onConfirm={data => onDispute?.(data)}
        teamNameA={teamNameA}
        teamNameB={teamNameB}
        currentServer={currentServer}
      />
      <ConfirmModal
        open={confirmReset}
        title="確認重新開始?"
        desc="所有比分將清除,無法復原。"
        confirmLabel="確認重新開始"
        onConfirm={() => {
          setConfirmReset(false);
          onReset();
        }}
        onCancel={() => setConfirmReset(false)}
      />
      <PricingModal open={showPricing} onClose={() => setShowPricing(false)} />
    </>
  );
};

export default TopBar;